// src/Pages/NewTask/NewTaskPageContent.jsx
import React, { useState, useEffect } from 'react';
import { Container, Button } from 'react-bootstrap';
import ParameterTabs from '../../Components/ParameterTabs/ParameterTabs';
import FileNameModal from '../../Components/FileNameModal/FileNameModal';
import { MsalAuthenticationTemplate, useMsal } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { loginRequest } from '../../authConfig';
import { DataPullerService } from '../../Services/DataPullerService';
import SelectPipelineModal from '../../Components/SelectPipelineModal/SelectPipelineModal';
import { useNavigate } from 'react-router-dom';
import { PageLayout2 } from '../../Components/PageLayout/PageLayout2';
import InformationModal from '../../Components/InformationModal/InformationModal';
import ConfirmationModal from '../../Components/ConfirmationModal/ConfirmationModal';

const NewTaskPageContent = () => {
    const { instance, accounts, inProgress } = useMsal();
    const [parameterGroups, setParameterGroups] = useState([]);
    // Now store selectedValues as map of paramId -> { id, name, values[] }
    const [selectedValues, setSelectedValues] = useState({});
    const [selectedPipeline, setSelectedPipeline] = useState(null);
    const [showFileNameModal, setShowFileNameModal] = useState(false);
    const [showPipelineModal, setShowPipelineModal] = useState(true);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [activeKey, setActiveKey] = useState(0);
    const [parameterOptions, setParameterOptions] = useState({});
    const navigate = useNavigate();

    useEffect(() => {
        if (selectedPipeline) fetchParameterGroups();
    }, [selectedPipeline]);

    useEffect(() => {
        // fetch options for each parameter based on current selections
        parameterGroups.forEach(group => {
            group.parameters.forEach(param => {
                fetchParameterValues(param.id, 
                    Object.values(selectedValues).map(sv => ({ id: sv.id, values: sv.values }))
                );
            });
        });
    }, [parameterGroups]);

    const fetchParameterValues = async (parameterId, relatedParameterValues) => {
        try {
            const request = { ...loginRequest, account: accounts[0] };
            const response = await instance.acquireTokenSilent(request);
            const data = await DataPullerService.getParameterValues(parameterId, relatedParameterValues, response.accessToken);
            setParameterOptions(prev => ({ ...prev, [parameterId]: data }));
        } catch (error) {
            console.error('Failed to fetch parameter values:', error);
        }
    };

    // Inside your NewTaskPageContent component:

    const handleValueChange = (parameter, value) => {
        // 1) Compute the new entry for this parameter
        const existing = selectedValues[parameter.id] || {
        id: parameter.id,
        name: parameter.name,
        values: []
        };
        let newVals = [...existing.values];
    
        if (value === '-1') {
        // Toggle “All”
        newVals = existing.values.includes('-1') ? [] : ['-1'];
        } else {
        // Expand “All” into every option if previously selected
        if (newVals.includes('-1')) {
            newVals = (parameterOptions[parameter.id] || [])
            .map(opt => opt.id.toString());
        }
        // Toggle this single value
        if (newVals.includes(value)) {
            newVals = newVals.filter(v => v !== value);
        } else {
            newVals.push(value);
        }
        // If all options are selected, collapse to “All”
        if (newVals.length === (parameterOptions[parameter.id]?.length || 0)) {
            newVals = ['-1'];
        }
        }
    
        // 2) Build the updated map of selections
        const newSelectedValues = {
        ...selectedValues,
        [parameter.id]: { 
            id: parameter.id,
            name: parameter.name,
            values: newVals 
        }
        };
    
        // 3) Update state
        setSelectedValues(newSelectedValues);
    
        // 4) Prepare full payload (with id, name, values) for dependent fetches
        const relatedPayload = Object.values(newSelectedValues);
    
        // 5) Refresh any parameters whose relatedParameters include this one
        parameterGroups.forEach(group => {
        group.parameters.forEach(p => {
            if (p.relatedParameters && p.relatedParameters.includes(parameter.id)) {
            fetchParameterValues(p.id, relatedPayload);
            }
        });
        });
    };
  

    const fetchParameterGroups = async () => {
        try {
            const request = { ...loginRequest, account: accounts[0] };
            const response = await instance.acquireTokenSilent(request);
            const data = await DataPullerService.getParameterGroups(selectedPipeline, response.accessToken);
            setParameterGroups(data);
        } catch (error) {
            console.error('Failed to fetch parameter groups:', error);
        }
    };

    const handlePipelineSelect = (pipelineId) => {
        setSelectedPipeline(pipelineId);
        setShowPipelineModal(false);
    };

    const handleSaveParameters = () => {
        setShowFileNameModal(true);
    };

    const handleFileNameSubmit = async (fileName, saveAsTemplate) => {
        try {
            const request = { ...loginRequest, account: accounts[0] };
            const response = await instance.acquireTokenSilent(request);
            const paramsArray = Object.values(selectedValues).map(sv => ({
                Id: sv.id,
                Name: sv.name,
                Values: sv.values
            }));
            const taskPayload = {
                TaskName: fileName,
                PipelineId: Number(selectedPipeline),
                Parameters: paramsArray
            };
            if (saveAsTemplate) {
                await DataPullerService.saveTemplate({ ...taskPayload, IsDynamic: true }, response.accessToken);
                navigate('/extraction-templates');
            } else {
                await DataPullerService.executeTask(taskPayload, response.accessToken);
                navigate('/data-extraction');
            }
        } catch (error) {
            console.error('Failed to create task:', error);
        }
    };

    const handleCancel = () => setShowConfirmationModal(true);
    const handleConfirmBack = () => navigate('/data-extraction');
    const handleCloseErrorModal = () => { setShowErrorModal(false); navigate('/data-extraction'); };

    return (
        <Container fluid className='d-flex flex-column flex-grow-1 h-100 NewTaskPage p-4 justify-content-stretch'>
            <ParameterTabs
                parameterGroups={parameterGroups}
                selectedValues={selectedValues}
                setSelectedValues={handleValueChange}
                onSave={handleSaveParameters}
                activeKey={activeKey}
                handleTabSelect={k => setActiveKey(+k)}
                handleValueChange={handleValueChange}
                parameterOptions={parameterOptions}
            />
            <div className='w-100 d-flex justify-content-between'>
                <Button variant='outline-dark' size='lg' onClick={handleCancel}>Cancel</Button>
                <div>
                    <Button variant='secondary' size='lg' className='me-4' onClick={() => activeKey>0 && setActiveKey(activeKey-1)}>Previous</Button>
                    <Button variant='primary' size='lg' onClick={() => activeKey < parameterGroups.length-1 ? setActiveKey(activeKey+1) : handleSaveParameters()}>
                        {activeKey < parameterGroups.length-1 ? 'Next' : 'Finish'}
                    </Button>
                </div>
            </div>

            <FileNameModal show={showFileNameModal} onHide={() => setShowFileNameModal(false)} onSubmit={handleFileNameSubmit} allowTemplate={true} />
            <SelectPipelineModal show={showPipelineModal} onPipelineSelect={handlePipelineSelect} />
            <InformationModal show={showErrorModal} onAcceptClick={handleCloseErrorModal} onHide={handleCloseErrorModal}
                title="Error running task" content="An error occurred processing the data extraction request." />
            <ConfirmationModal show={showConfirmationModal} onAcceptClick={handleConfirmBack}
                onCancelClick={() => setShowConfirmationModal(false)} onHide={() => setShowConfirmationModal(false)}
                title="Exit Selector" content="Your selections will be lost. Continue?" />
        </Container>
    );
};

const NewTaskPage = () => (
    <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={loginRequest}>
        <PageLayout2 title="New Data Selector Task">
            <NewTaskPageContent />
        </PageLayout2>
    </MsalAuthenticationTemplate>
);

export default NewTaskPage;
